import {
  CheckboxActive,
  CheckboxDefault,
  CheckboxDeselected,
} from '@/assets/svg';
import { Box, CircularProgress } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core/styles';
import React, { memo, useMemo, useCallback } from 'react';
import { FixedSizeList as List } from 'react-window';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    position: 'relative',
  },
  table: {
    minWidth: 650,
    overflow: 'auto',
    '& .MuiTableRow-head': {
      height: 40,
      border: 'none',
      display: 'grid',
      gridTemplateColumns: '50px 1fr 100px 1fr 100px', // checkbox, package, type, price, action
      backgroundColor: '#f6f6f6',

      '& .MuiTableCell-root': {
        fontSize: 12,
        fontWeight: 400,
        padding: '8px 12px',
        backgroundColor: '#f6f6f6',
        border: 'none',
      },
    },

    '& .MuiTableRow-root.Mui-selected': {
      backgroundColor: 'unset',
      '&:hover': {
        backgroundColor: theme.palette.grey.background,
      },
    },
  },
  virtualizedRow: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 100px 1fr 100px', // checkbox, package, type, price, action
    alignItems: 'center',
    height: '100%',
    borderBottom: '1px solid #e0e0e0',
    '&:hover': {
      backgroundColor: theme.palette.grey.background,
    },
  },
  checkboxCell: {
    padding: '0 4px 0 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dataCell: {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
  },
  actionCell: {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const VirtualizedRow = memo(({ index, style, data }) => {
  const { items, renderRow, selected, onSelectChange, isSelected } = data;
  const classes = useStyles();
  const item = items[index];
  const itemSelected = isSelected(item.id);

  const handleClick = useCallback(
    (event) => {
      const selectedIndex = selected.indexOf(item.id);
      let updatedSelected = [];

      if (selectedIndex === -1) {
        updatedSelected = [...selected, item.id];
      } else {
        updatedSelected = selected.filter((id) => id !== item.id);
      }

      onSelectChange(updatedSelected);
    },
    [item.id, selected, onSelectChange]
  );

  // Render columns from renderRow function with specific widths
  const renderColumns = () => {
    const rowCells = renderRow(item, index);

    // Extract children from React Fragment
    const children = rowCells?.props?.children || rowCells;

    // If children array exists (React Fragment)
    if (Array.isArray(children)) {
      return children.map((cell, colIndex) => {
        // Extract content from TableCell
        const cellContent = cell?.props?.children || cell;

        // Action column (last column) uses actionCell class
        const cellClass =
          colIndex === children.length - 1
            ? classes.actionCell
            : classes.dataCell;

        return (
          <div key={colIndex} className={cellClass}>
            {cellContent}
          </div>
        );
      });
    }

    // Fallback if renderRow returns single element
    return <div className={classes.dataCell}>{rowCells}</div>;
  };

  return (
    <div style={style}>
      <div className={classes.virtualizedRow}>
        <div className={classes.checkboxCell}>
          <Checkbox
            color="primary"
            checked={itemSelected}
            onClick={handleClick}
            inputProps={{
              'aria-labelledby': `enhanced-table-checkbox-${index}`,
            }}
            icon={<CheckboxDefault width={20} height={20} />}
            checkedIcon={<CheckboxActive width={20} height={20} />}
          />
        </div>
        {renderColumns()}
      </div>
    </div>
  );
});

VirtualizedRow.displayName = 'VirtualizedRow';

const VirtualizedTable = ({
  data,
  columns,
  renderRow,
  selected,
  onSelectChange,
  onUnSelectAll,
  height = 400,
  itemHeight = 60,
  hasMore = false,
  loadMore,
  loading = false,
  showModal = true,
  filterColumnIndex = 3, // Index of column to filter when showModal = true
  ...props
}) => {
  const classes = useStyles();

  // Filter renderRow to remove Visibility Switch column when showModal = true
  const filteredRenderRow = useCallback(
    (item, index) => {
      const rowCells = renderRow(item, index);
      const children = rowCells?.props?.children || rowCells;

      if (Array.isArray(children) && showModal && filterColumnIndex !== null) {
        // Remove column at filterColumnIndex when showModal = true
        const filteredChildren = children.filter(
          (_, colIndex) => colIndex !== filterColumnIndex
        );
        return {
          ...rowCells,
          props: { ...rowCells.props, children: filteredChildren },
        };
      }

      return rowCells;
    },
    [renderRow, showModal, filterColumnIndex]
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const isSelected = useCallback((id) => selectedSet.has(id), [selectedSet]);

  const handleSelectAllClick = useCallback(
    (event) => {
      if (event.target.checked) {
        const newSelected = data.map((n) => n.id);
        onSelectChange(newSelected);
        return;
      }
      onUnSelectAll();
    },
    [data, onSelectChange, onUnSelectAll]
  );

  const itemData = useMemo(
    () => ({
      items: data,
      renderRow: filteredRenderRow,
      selected,
      onSelectChange,
      isSelected,
    }),
    [data, filteredRenderRow, selected, onSelectChange, isSelected]
  );

  // Handle infinite scroll
  const handleItemsRendered = useCallback(
    ({ visibleStopIndex }) => {
      // Load more when user scrolls near the end
      if (hasMore && !loading && visibleStopIndex >= data.length - 5) {
        loadMore && loadMore();
      }
    },
    [hasMore, loading, data.length, loadMore]
  );

  return (
    <Paper square variant="outlined" id={props.id}>
      <TableContainer className={classes.tableContainer} {...props}>
        <Table className={classes.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ padding: '0 4px 0', width: 50 }}>
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < data.length
                  }
                  checked={data.length > 0 && selected.length === data.length}
                  onChange={handleSelectAllClick}
                  icon={<CheckboxDefault width={20} height={20} />}
                  checkedIcon={<CheckboxActive width={20} height={20} />}
                  indeterminateIcon={
                    <CheckboxDeselected width={20} height={20} />
                  }
                />
              </TableCell>
              {columns.map((column, index) => (
                <TableCell
                  key={column.id || column.stateValue || column.label || index}
                  style={{
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                    width: column.width,
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        </Table>
        <List
          height={height - 40}
          itemCount={data.length}
          itemSize={itemHeight}
          itemData={itemData}
          overscanCount={5}
          onItemsRendered={handleItemsRendered}
          style={{
            overflowX: 'hidden',
          }}
        >
          {VirtualizedRow}
        </List>
        {loading && hasMore && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={2}
          >
            <CircularProgress color="primary" size={20} />
          </Box>
        )}
      </TableContainer>
    </Paper>
  );
};

export default memo(VirtualizedTable);
